/**
 * @file Recalls advanced-search modal and validation.
 */

import { appData } from "../../app/state/state.js";
import {
    bindNativeFieldBehavior,
    NATIVE_CHECKBOX_CLASSES,
    NATIVE_CHECKBOX_ICON_CLASSES,
    NATIVE_CHECKBOX_LABEL_CLASSES,
    NATIVE_CHECKBOX_MARKER_CLASSES,
    getNativeFieldWrapperHtml,
    NATIVE_INPUT_CLASSES,
} from "../../shared/forms/nativeFieldStyles.js";
import { closeForm } from "../../shared/forms/modalShell.js";
import { openStandardForm } from "../../shared/forms/standardFormModal.js";
import {
    createProviderSelectPreset,
    createStatusSelectPreset,
} from "../../shared/ui/controls/controlPresets.js";
import { createDateControl } from "../../shared/ui/controls/dateControl.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import { advancedSearchRecalls } from "./recallsApi.js";
import {
    setRecallsAdvancedSearchError,
    setRecallsAdvancedSearchResults,
} from "./recallsState.js";
import {
    RECALLS_MODAL_BODY_SPACING_CLASSES,
    RECALLS_MODAL_GRID_CLASSES,
    setRecallsModalActionProcessing,
} from "./recallsModalLayout.js";

const TEXT_FIELD_KEYS = Object.freeze(["firstName", "lastName", "email", "phone"]);
const EMAIL_DOMAIN_ONLY_MESSAGE = "Search by email name or full email address.";
const MIN_TEXT_MESSAGE = "Use at least 3 characters.";
const FORM_EMPTY_MESSAGE = "Add at least one search field.";

let hasAttemptedValidation = false;
let blurredTextFields = new Set();

function attrValue(value) {
    return escapeHTML(String(value ?? ""))
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function normalizeSearchText(value) {
    const normalized = String(value || "").trim();
    if (!normalized) {return "";}
    return normalized
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, " ");
}

function normalizePhoneSearchText(value) {
    return String(value || "").replace(/\D+/g, "");
}

function parseArrayValue(value) {
    if (Array.isArray(value)) {return value.filter(Boolean);}
    if (!value) {return [];}
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch {
        return [value].filter(Boolean);
    }
}

function getReasonOptions() {
    return (appData.config?.recalls?.recallReasons || [])
        .filter((option) => option?.id && option?.isActive !== false)
        .map((option) => ({
            value: option.id,
            label: option.name || option.id,
            source: option,
        }));
}

function getCriteriaValue(criteria, key, fallback = "") {
    return criteria?.[key] ?? criteria?.[key.replace(/[A-Z]/g, (match) =>
        `_${match.toLowerCase()}`)] ?? fallback;
}

function getProviderCriteriaValues(criteria = {}) {
    return parseArrayValue(criteria.provider || criteria.providerIds)
        .concat(parseArrayValue(criteria.providerUserIds))
        .map((value) => String(value || "").replace(/^app:/, ""))
        .filter((value) => value && !value.startsWith("legacy:"));
}

function getTextInputHtml({ name, placeholder, type = "text", value = "" }) {
    return `
        <input
            type="${attrValue(type)}"
            name="${attrValue(name)}"
            value="${attrValue(value)}"
            placeholder="${attrValue(placeholder)}"
            class="${NATIVE_INPUT_CLASSES}"
            data-recalls-advanced-input="${attrValue(name)}"
        >
        <p class="hidden text-xs font-medium text-red-600" data-recalls-advanced-field-error="${attrValue(name)}"></p>
    `;
}

function getSelectMirrorHtml({ name, value = [] }) {
    return `
        <div data-recalls-advanced-${attrValue(name)}-control></div>
        <input
            type="hidden"
            name="${attrValue(name)}"
            value="${attrValue(JSON.stringify(value))}"
            data-recalls-advanced-${attrValue(name)}-mirror
        >
    `;
}

function getDateControlHtml({ name, value = "" }) {
    return `
        <div data-recalls-advanced-${attrValue(name)}-control></div>
        <input
            type="hidden"
            name="${attrValue(name)}"
            value="${attrValue(value)}"
            data-recalls-advanced-${attrValue(name)}-mirror
        >
    `;
}

function getShowClosedHtml(checked) {
    return `
        <label class="${NATIVE_CHECKBOX_LABEL_CLASSES}" data-native-checkbox-label>
            <input
                type="checkbox"
                name="showClosed"
                class="${NATIVE_CHECKBOX_CLASSES}"
                ${checked ? "checked" : ""}
                data-recalls-advanced-input="showClosed"
            >
            <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
                <span class="${NATIVE_CHECKBOX_ICON_CLASSES} ${checked ? "" : "opacity-0"}" data-native-checkbox-icon>check</span>
            </span>
            <span>Show closed</span>
        </label>
    `;
}

function getAdvancedSearchFieldsHtml(criteria = {}) {
    return `
        <div class="space-y-4 ${RECALLS_MODAL_BODY_SPACING_CLASSES}" data-recalls-advanced-search-fields>
            <div class="${RECALLS_MODAL_GRID_CLASSES}">
                ${getNativeFieldWrapperHtml("First Name", getTextInputHtml({
                    name: "firstName",
                    placeholder: "First name",
                    value: getCriteriaValue(criteria, "firstName"),
                }))}
                ${getNativeFieldWrapperHtml("Last Name", getTextInputHtml({
                    name: "lastName",
                    placeholder: "Last name",
                    value: getCriteriaValue(criteria, "lastName"),
                }))}
                ${getNativeFieldWrapperHtml("Email", getTextInputHtml({
                    name: "email",
                    placeholder: "patient@example.com",
                    type: "email",
                    value: criteria.email || "",
                }))}
                ${getNativeFieldWrapperHtml("Phone", getTextInputHtml({
                    name: "phone",
                    placeholder: "Phone",
                    type: "tel",
                    value: criteria.phone || "",
                }))}
                ${getNativeFieldWrapperHtml("Recall Date From", getDateControlHtml({
                    name: "recallDateFrom",
                    value: criteria.recallDateFrom || criteria.dateFrom || "",
                }))}
                ${getNativeFieldWrapperHtml("Recall Date To", getDateControlHtml({
                    name: "recallDateTo",
                    value: criteria.recallDateTo || criteria.dateTo || "",
                }))}
                ${getNativeFieldWrapperHtml("Provider", getSelectMirrorHtml({
                    name: "provider",
                    value: getProviderCriteriaValues(criteria),
                }))}
                ${getNativeFieldWrapperHtml("Reason", getSelectMirrorHtml({
                    name: "reason",
                    value: parseArrayValue(
                        criteria.reason || criteria.reasonIds || criteria.recall_reason,
                    ),
                }))}
                ${getNativeFieldWrapperHtml("Status", getSelectMirrorHtml({
                    name: "status",
                    value: parseArrayValue(criteria.status || criteria.statusIds),
                }))}
                <div class="flex min-h-10 items-center md:pt-6">${getShowClosedHtml(criteria.showClosed === true)}</div>
            </div>
            <p class="hidden rounded-md border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700" data-recalls-advanced-form-error></p>
        </div>
    `;
}

function mountReasonControl(itemForm) {
    const root = itemForm.querySelector("[data-recalls-advanced-reason-control]");
    const mirrorInput = itemForm.querySelector("[data-recalls-advanced-reason-mirror]");
    if (!root || !mirrorInput) {return;}
    createSelectControl({
        root,
        mirrorInput,
        name: "reason",
        value: parseArrayValue(mirrorInput.value),
        options: getReasonOptions(),
        placeholder: "+ Add reason",
        multiple: true,
        clearable: true,
        portalMenu: true,
    });
}

function mountStatusControl(itemForm) {
    const root = itemForm.querySelector("[data-recalls-advanced-status-control]");
    const mirrorInput = itemForm.querySelector("[data-recalls-advanced-status-mirror]");
    if (!root || !mirrorInput) {return;}
    createStatusSelectPreset({
        root,
        appData,
        configKey: "recallStatus",
        mirrorInput,
        name: "status",
        value: parseArrayValue(mirrorInput.value),
        placeholder: "+ Add status",
        multiple: true,
        clearable: true,
        portalMenu: true,
    });
}

function mountProviderControl(itemForm) {
    const root = itemForm.querySelector("[data-recalls-advanced-provider-control]");
    const mirrorInput = itemForm.querySelector("[data-recalls-advanced-provider-mirror]");
    if (!root || !mirrorInput) {return;}
    createProviderSelectPreset({
        root,
        appData,
        mirrorInput,
        name: "provider",
        value: parseArrayValue(mirrorInput.value),
        searchable: true,
        portalMenu: true,
    });
}

function mountDateControls(itemForm) {
    ["recallDateFrom", "recallDateTo"].forEach((name) => {
        const root = itemForm.querySelector(`[data-recalls-advanced-${name}-control]`);
        const mirrorInput = itemForm.querySelector(
            `[data-recalls-advanced-${name}-mirror]`,
        );
        if (!root || !mirrorInput) {return;}
        createDateControl({
            root,
            mirrorInput,
            name,
            type: "date",
            value: mirrorInput.value || "",
        });
    });
}

function collectAdvancedSearchCriteria(itemForm) {
    const getInput = (name) => itemForm.querySelector(`[name="${name}"]`);
    return {
        email: getInput("email")?.value?.trim() || "",
        firstName: getInput("firstName")?.value?.trim() || "",
        lastName: getInput("lastName")?.value?.trim() || "",
        phone: getInput("phone")?.value?.trim() || "",
        provider: parseArrayValue(getInput("provider")?.value),
        reason: parseArrayValue(getInput("reason")?.value),
        recallDateFrom: getInput("recallDateFrom")?.value || "",
        recallDateTo: getInput("recallDateTo")?.value || "",
        showClosed: getInput("showClosed")?.checked === true,
        status: parseArrayValue(getInput("status")?.value),
    };
}

function pruneAdvancedSearchCriteria(criteria) {
    const pruned = {};
    Object.entries(criteria || {}).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            if (value.length > 0) {pruned[key] = value;}
            return;
        }
        if (key === "showClosed") {
            if (value === true) {pruned[key] = true;}
            return;
        }
        if (String(value || "").trim()) {
            pruned[key] = value;
        }
    });
    return pruned;
}

function hasSearchCriterion(criteria) {
    return Boolean(
        criteria.firstName ||
        criteria.lastName ||
        criteria.email ||
        criteria.phone ||
        criteria.reason?.length > 0 ||
        criteria.status?.length > 0 ||
        criteria.provider?.length > 0 ||
        criteria.recallDateFrom ||
        criteria.recallDateTo,
    );
}

function setFieldError(itemForm, fieldName, message = "", { visible = true } = {}) {
    const input = itemForm.querySelector(`[name="${fieldName}"]`);
    const messageEl = itemForm.querySelector(
        `[data-recalls-advanced-field-error="${fieldName}"]`,
    );
    input?.setCustomValidity(message || "");
    input?.classList.toggle("border-red-500", Boolean(message && visible));
    input?.setAttribute("aria-invalid", message && visible ? "true" : "false");
    if (!messageEl) {return;}
    messageEl.textContent = visible ? message : "";
    messageEl.classList.toggle("hidden", !message || !visible);
}

function setFormError(itemForm, message = "") {
    const messageEl = itemForm.querySelector("[data-recalls-advanced-form-error]");
    if (!messageEl) {return;}
    messageEl.textContent = message;
    messageEl.classList.toggle("hidden", !message);
}

function validateAdvancedSearchForm(
    itemForm,
    { showFieldErrors = false, showFormErrors = false } = {},
) {
    const criteria = collectAdvancedSearchCriteria(itemForm);
    const errors = {};
    TEXT_FIELD_KEYS.forEach((key) => {
        const rawValue = criteria[key] || "";
        const normalized = key === "phone" ?
            normalizePhoneSearchText(rawValue) :
            normalizeSearchText(rawValue);
        if (!rawValue) {return;}
        if (key === "email" && rawValue.startsWith("@")) {
            errors[key] = EMAIL_DOMAIN_ONLY_MESSAGE;
            return;
        }
        if (normalized.length < 3) {
            errors[key] = MIN_TEXT_MESSAGE;
        }
    });
    if (
        criteria.recallDateFrom &&
        criteria.recallDateTo &&
        criteria.recallDateFrom > criteria.recallDateTo
    ) {
        errors.form = "Recall date to must be after recall date from.";
    }

    TEXT_FIELD_KEYS.forEach((key) => setFieldError(itemForm, key, errors[key] || "", {
        visible: showFieldErrors || blurredTextFields.has(key),
    }));
    const validFields = Object.keys(errors).filter((key) => key !== "form").length === 0;
    const hasCriterion = hasSearchCriterion(criteria);
    setFormError(itemForm, showFormErrors ?
        (errors.form || (hasCriterion ? "" : FORM_EMPTY_MESSAGE)) :
        "");
    return {
        criteria: pruneAdvancedSearchCriteria(criteria),
        valid: validFields && !errors.form && hasCriterion,
        validFields: validFields && !errors.form,
    };
}

function syncSubmitState(itemForm) {
    const submitBtn = itemForm.querySelector("button[type=\"submit\"]");
    const validation = validateAdvancedSearchForm(itemForm, {
        showFormErrors: hasAttemptedValidation,
    });
    if (!submitBtn) {return validation;}
    submitBtn.disabled = !validation.validFields;
    submitBtn.classList.toggle("opacity-50", !validation.validFields);
    submitBtn.classList.toggle("cursor-not-allowed", !validation.validFields);
    return validation;
}

function applyCallableFieldErrors(itemForm, error) {
    const details = Array.isArray(error?.details) ? error.details : [];
    let handled = false;
    details.forEach((entry) => {
        if (!entry?.field || !entry?.message) {return;}
        if (TEXT_FIELD_KEYS.includes(entry.field)) {
            setFieldError(itemForm, entry.field, entry.message);
            handled = true;
            return;
        }
        setFormError(itemForm, entry.message);
        handled = true;
    });
    if (!handled) {
        setFormError(itemForm, error?.message || "Advanced search could not be completed.");
    }
}

/**
 * Opens the Recalls advanced-search modal.
 * @param {object} [options={}] Modal options.
 * @param {object} [options.criteria={}] Initial criteria.
 * @param {Function} [options.onComplete] Callback after state changes.
 * @param {Function} [options.searchRecalls] Search callable override for tests.
 * @returns {void}
 */
export function openRecallAdvancedSearchModal({
    criteria = {},
    onComplete = null,
    searchRecalls = advancedSearchRecalls,
} = {}) {
    hasAttemptedValidation = false;
    blurredTextFields = new Set();
    openStandardForm("recalls_advanced_search", criteria, {
        getFieldsHtml: getAdvancedSearchFieldsHtml,
        afterRender({ itemForm }) {
            const title = document.getElementById("form-title");
            const submitBtn = itemForm.querySelector("button[type=\"submit\"]");
            if (title) {title.textContent = "Advanced Search";}
            if (submitBtn) {submitBtn.textContent = "Search";}
            bindNativeFieldBehavior(itemForm);
            mountReasonControl(itemForm);
            mountStatusControl(itemForm);
            mountProviderControl(itemForm);
            mountDateControls(itemForm);
            syncSubmitState(itemForm);

            itemForm.addEventListener("input", () => syncSubmitState(itemForm));
            itemForm.addEventListener("change", () => syncSubmitState(itemForm));
            TEXT_FIELD_KEYS.forEach((key) => {
                itemForm.querySelector(`[name="${key}"]`)
                    ?.addEventListener("blur", () => {
                        blurredTextFields.add(key);
                        syncSubmitState(itemForm);
                    });
            });
            itemForm.onsubmit = async (event) => {
                event.preventDefault();
                hasAttemptedValidation = true;
                const validation = validateAdvancedSearchForm(itemForm, {
                    showFieldErrors: true,
                    showFormErrors: true,
                });
                if (!validation.valid) {return;}
                try {
                    setRecallsModalActionProcessing(submitBtn, true, {
                        processingLabel: "Searching...",
                    });
                    setFormError(itemForm, "");
                    const response = await searchRecalls({
                        criteria: validation.criteria,
                    });
                    setRecallsAdvancedSearchResults({
                        cap: response.cap ?? null,
                        criteria: validation.criteria,
                        reachedCap: response.reachedCap === true,
                        rows: response.rows || [],
                        total: response.total ?? response.rows?.length ?? 0,
                    });
                    closeForm();
                    onComplete?.();
                } catch (error) {
                    console.error("Error running Recalls advanced search:", error);
                    setRecallsAdvancedSearchError({
                        criteria: validation.criteria,
                        error: error?.message || null,
                    });
                    applyCallableFieldErrors(itemForm, error);
                } finally {
                    if (submitBtn && itemForm.isConnected) {
                        setRecallsModalActionProcessing(submitBtn, false, {
                            idleLabel: "Search",
                        });
                        syncSubmitState(itemForm);
                    }
                }
            };
        },
    });
}
